import { useAuth } from "@/context/AuthContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * Renders a user by EMAIL as their display NAME (the primary identifier), with the email
 * still reachable for traceability:
 *   - inline: "Name (email)" (traceability-heavy screens with room)
 *   - default: Name primary; email via hover tooltip (desktop) OR tap popover (touch)
 * Falls back to the raw email for legacy / unresolved actors (no current user record).
 */
export function UserName({ email, inline = false, plain = false, className = "", testid }) {
  const { resolveName } = useAuth();
  if (!email) return <span className={className} data-testid={testid}>—</span>;
  const name = resolveName(email);
  const unresolved = name === email; // no distinct name on record

  if (inline) {
    return (
      <span className={className} data-testid={testid}>
        {unresolved ? email : <>{name} <span className="text-slate-400 font-normal">({email})</span></>}
      </span>
    );
  }

  // plain: name-primary span with hover tooltip only (no Popover button) — safe inside a clickable row/button.
  if (plain || unresolved) {
    return <span className={className} title={email} data-testid={testid}>{name}</span>;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={email}
          data-testid={testid}
          onClick={(e) => e.stopPropagation()}
          className={`text-left hover:underline decoration-dotted underline-offset-2 cursor-pointer ${className}`}
        >
          {name}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" onClick={(e) => e.stopPropagation()} className="w-auto max-w-[260px] p-2 text-xs">
        <div className="font-semibold text-slate-800">{name}</div>
        <div className="font-mono text-slate-500 break-all">{email}</div>
      </PopoverContent>
    </Popover>
  );
}
